import { inject, Injectable, OnDestroy } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { AlbumImage } from '../../models/image.album.model';
import { UserModel } from '../../models/user.model';
import { AlbumService } from '../../services/album.service';
import { AuthService } from '../../services/auth.service';
import { ConnectionService } from '../../services/connection.service';
import * as albumImageActions from '../actions/image.album.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';

@Injectable()
export class AlbumImageEffects implements OnDestroy {
  actions$ = inject(Actions);
  private connectionService = inject(ConnectionService);
  store: Store<AppState> = inject(Store<AppState>);
  private albumService = inject(AlbumService);
  private authService = inject(AuthService);
  private logger: NGXLogger = inject(NGXLogger);
  toastr: ToastrService = inject(ToastrService);
  afs: AngularFirestore = inject(AngularFirestore);
  router: Router = inject(Router);

  user: UserModel | null | undefined;

  private logout$ = new Subject<void>();

  constructor() {}

  ngOnDestroy(): void {
    this.logout$.next();
    this.logout$.complete();
  }

  getAlbumsImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(albumImageActions.getAlbumImages),
      debounceTime(500),
      switchMap(({ groupId, albumId, lastRecord, limitRecords }) => {
        this.logger.debug('Effect: getAlbumsImages$', {
          groupId,
          albumId,
          lastRecord,
          limitRecords,
        });
        return this.albumService
          .getAlbumImages({
            groupId: groupId,
            albumId: albumId,
            lastRecord: lastRecord,
            limitRecords: limitRecords,
          })
          .pipe(
            map((images: AlbumImage[]) => {
              return albumImageActions.addManyToStore({
                images: images,
              });
            }),
            catchError((error) => {
              this.logger.error('API Error Status:', error.status);
              return of(
                albumImageActions.getAlbumImagesFailure({
                  error,
                }),
              );
            }),
          );
      }),
    ),
  );

  deleteImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(albumImageActions.deleteImage),
      switchMap(({ groupId, albumId, imageId }) => {
        this.logger.debug('Effect: deleteImage$', {
          groupId,
          albumId,
          imageId,
        });

        // Wrap the promise in 'from' to turn it into an Observable
        return from(
          this.albumService.deleteImage({ groupId, albumId, imageId }),
        ).pipe(
          map((data: any) => {
            if (data.success) {
              return albumImageActions.deleteSuccess({ groupId, imageId });
            } else {
              // Business logic "failure" (e.g., success: false)
              return albumImageActions.deleteFailure({ error: data.message });
            }
          }),
          catchError((error: any) => {
            // Actual HTTP or Network error
            this.logger.error('API Error Status:', error.status);
            return of(albumImageActions.deleteFailure({ error }));
          }),
        );
      }),
    ),
  );

  clearAlbumsImages$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(albumImageActions.clear());
      }),
    ),
  );
}
